import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './PaymentSystem.css';

/**
 * PricingTiers - 2025 Cash App-inspired Design
 *
 * 3-tier structure: Free / Unlimited (featured) / Home
 * Educator tier moved to separate B2B flow
 */
const PricingTiers = ({ userTier = 'guest', currentUser, onDirectCheckout, loading }) => {
  const [selectedTierId, setSelectedTierId] = useState(null);
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('annual');

  // Simplified 3-tier pricing
  const tiers = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: '',
      description: 'Try it out',
      features: [
        '3 voice tutoring sessions',
        'All subjects available',
        'Join friends with paid plans',
        'No credit card required'
      ],
      cta: 'Start free',
      ctaStyle: 'secondary',
      featured: false
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      price: billingCycle === 'annual' ? '$89' : '$9.99',
      period: billingCycle === 'annual' ? '/year' : '/mo',
      description: 'Everything you need',
      features: [
        'Unlimited voice tutoring',
        'Screen sharing for homework',
        'Study with friends',
        'Progress tracking',
        'Priority support'
      ],
      cta: 'Get started',
      ctaStyle: 'primary',
      featured: true,
      savings: billingCycle === 'annual' ? 'Save 26%' : null
    },
    {
      id: 'home',
      name: 'Home',
      price: billingCycle === 'annual' ? '$199' : '$19.99',
      period: billingCycle === 'annual' ? '/year' : '/mo',
      description: 'Up to 5 students',
      features: [
        'Everything in Unlimited',
        '5 student accounts',
        'Parent dashboard',
        'Family leaderboards',
        'Sibling collaboration bonuses'
      ],
      cta: 'Get started',
      ctaStyle: 'secondary',
      featured: false,
      savings: billingCycle === 'annual' ? 'Save $246/year' : null
    }
  ];

  const handleTierSelect = (tierId) => {
    // Analytics
    if (window.posthog) {
      try {
        window.posthog.capture('pricing_tier_selected', {
          tier: tierId,
          current_tier: userTier,
          billing_cycle: billingCycle
        });
      } catch (e) {}
    }

    if (tierId === 'free') {
      if (!currentUser) {
        navigate('/login?mode=signup&tier=free');
      } else {
        navigate('/tutor');
      }
    } else {
      const billingParam = billingCycle === 'monthly' ? 'monthly' : 'annual';
      // Direct checkout when rendered inside TutorInterface modal
      if (onDirectCheckout && currentUser) {
        setSelectedTierId(tierId);
        onDirectCheckout(tierId, billingParam);
        return;
      }
      if (!currentUser) {
        navigate(`/login?mode=signup&tier=${tierId}&billing=${billingParam}`);
      } else {
        navigate(`/upgrade?tier=${tierId}&billing=${billingParam}`);
      }
    }
  };

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        {/* Header */}
        <header className="pricing-header">
          <motion.h1
            className="pricing-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Simple, transparent pricing
          </motion.h1>
          <motion.p
            className="pricing-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Start free, upgrade when you're ready
          </motion.p>

          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
          >
            <div className="billing-toggle">
              <button
                className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingCycle('annual')}
              >
                Annual
              </button>
            </div>
            {billingCycle === 'annual' && (
              <span className="billing-savings">Save up to 26%</span>
            )}
          </motion.div>
        </header>

        {/* Pricing Cards */}
        <motion.div
          className="pricing-grid"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              className={`price-card ${tier.featured ? 'featured' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              whileHover={{ y: -4 }}
            >
              {tier.featured && (
                <div className="card-badge">Most popular</div>
              )}

              <p className="card-name">{tier.name}</p>

              <div className="card-price">
                {tier.price}
                {tier.period && <span>{tier.period}</span>}
              </div>

              <p className="card-desc">{tier.description}</p>

              {tier.savings && (
                <p style={{
                  fontSize: '13px',
                  color: '#10b981',
                  fontWeight: 600,
                  marginBottom: '16px'
                }}>
                  {tier.savings}
                </p>
              )}

              <ul className="card-features">
                {tier.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>

              <button
                className={`card-cta ${tier.ctaStyle}`}
                onClick={() => handleTierSelect(tier.id)}
                disabled={loading}
              >
                {loading && selectedTierId === tier.id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner-dot" style={{
                      width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite', display: 'inline-block'
                    }} />
                    Processing...
                  </span>
                ) : tier.cta}
              </button>

              {userTier === tier.id && (
                <p style={{
                  fontSize: '13px',
                  color: 'var(--color-primary, #297A92)',
                  marginTop: '12px',
                  fontWeight: 500
                }}>
                  ✓ Current plan
                </p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* B2B Link */}
        <motion.div
          className="b2b-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>
            Are you a school or district?{' '}
            <a href="mailto:sales@teachtappy.com?subject=School/District Inquiry">
              Talk to our team →
            </a>
          </p>
        </motion.div>

        {/* Trust Signal */}
        <motion.div
          style={{
            textAlign: 'center',
            marginTop: '48px',
            padding: '24px',
            background: 'var(--color-bg-subtle, #f4f4f5)',
            borderRadius: '16px',
            maxWidth: '600px',
            margin: '48px auto 0'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <p style={{
            fontSize: '15px',
            color: 'var(--color-text-secondary, #71717a)',
            margin: 0,
            lineHeight: 1.6
          }}>
            <strong style={{ color: 'var(--color-text, #18181b)' }}>30-day money-back guarantee.</strong>
            {' '}Try risk-free. Cancel anytime. No questions asked.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingTiers;
